// Reads Role Master's Module Permissions matrix to decide what the signed-in
// user can see. Previously nothing in the frontend consulted this data — the
// sidebar/menu showed every module to every user and every route was
// reachable regardless of the user's role permissions.
import type { AuthUser } from '@/context/auth'
import type { Role } from '@/context/MasterDataContext'

export type ModuleName =
  | 'Masters' | 'Generator' | 'Consumer' | 'Mapping' | 'Leads' | 'Proposals'
  | 'Agreements' | 'Allocations' | 'DCR' | 'Accounts' | 'Finance' | 'Reports'

export type PermissionAction = 'view' | 'add' | 'edit' | 'delete' | 'email' | 'download' | 'approve' | 'reject'

// Same case-insensitive "admin" escape hatch used everywhere else in the app
// (authStore.isAdmin(), the backend's isAdminRoleName()) — an admin-role user
// always has every permission, independent of Role Master data.
export function isAdminRoleName(roleName?: string | null): boolean {
  return String(roleName || '').trim().toLowerCase() === 'admin'
}

// users.role has no FK to roles.id — matched by name, case-insensitively,
// same loose linkage the rest of the app relies on.
function findRoleByName(roles: Role[], roleName?: string | null): Role | undefined {
  if (!roleName) return undefined
  const target = roleName.trim().toLowerCase()
  return roles.find((r) => r.status === 'active' && (r.name || '').trim().toLowerCase() === target)
}

export function hasModulePermission(
  user: Pick<AuthUser, 'role'> | null | undefined,
  roles: Role[],
  moduleName: ModuleName,
  action: PermissionAction = 'view',
): boolean {
  if (!user) return false
  if (isAdminRoleName(user.role)) return true
  const role = findRoleByName(roles, user.role)
  return !!role?.permissions?.[moduleName]?.[action]
}
